export type DomId =
  | "job_1_sidebar"
  | "job_1"
  | "job_2_sidebar"
  | "job_2"
  | "mediumrect_1_sidebar"
  | "mediumrect_1"
  | "mediumrect_2"
  | "mediumrect_3"
  | "sticky"
  | "top"
  | "billboard"
  | "billboard_mobile"
  | "sticky_halfpage"
  | "mediumrect_2_mobile"
  | "mediumrect_3_mobile"

/** Ad unit codes defined in GAM */
type AdUnitCode =
  | "job_1"
  | "job_2"
  | "mediumrect_1"
  | "mediumrect_2"
  | "mediumrect_3"
  | "sticky"
  | "top"
  | "billboard"
  | "sticky_halfpage"

type Breakpoints = "lg" | "xs" | "3xl"

export type AdMap = Record<number, googletag.GeneralSize>

type Settings = {
  readonly mapping: AdMap
  readonly sizes: googletag.GeneralSize
  readonly adUnitCode: AdUnitCode
}

type Advertisement = Record<DomId, Settings>

type BannerSize = {
  readonly large: googletag.SingleSizeArray
  readonly medium: googletag.SingleSizeArray
  readonly small: googletag.SingleSizeArray
  readonly sticky: googletag.SingleSizeArray
  readonly top: googletag.SingleSizeArray
}

const bannerSize: BannerSize = {
  // biome-ignore lint/style/noMagicNumbers: These are defined by business & GAM
  large: [300, 600],
  // biome-ignore lint/style/noMagicNumbers: These are defined by business & GAM
  medium: [300, 250],
  // biome-ignore lint/style/noMagicNumbers: These are defined by business & GAM
  small: [320, 160],
  // biome-ignore lint/style/noMagicNumbers: These are defined by business & GAM
  sticky: [160, 600],
  // biome-ignore lint/style/noMagicNumbers: These are defined by business & GAM
  top: [930, 180],
}

/** Unfortunately, GAM cannot handle rems, so we have to convert our tailwind breakpoints to px :( */
const mapTailwindBreakpointsToPx: Record<Breakpoints, number> = {
  "3xl": 1920, //  equals to 120rem, when the rem unit is 16px (the browser default).
  lg: 1024, // equals to 64rem, when the rem unit is 16px (the browser default).
  xs: 0,
}

const { lg, xs } = mapTailwindBreakpointsToPx
const threeXl = mapTailwindBreakpointsToPx["3xl"]

export const ads: Advertisement = {
  billboard: {
    adUnitCode: "billboard",
    mapping: {
      [threeXl]: [],
      [lg]: bannerSize.top,
      [xs]: [],
    },
    sizes: [bannerSize.top],
  },
  billboard_mobile: {
    adUnitCode: "billboard",
    mapping: {
      [threeXl]: [],
      [lg]: [],
      [xs]: bannerSize.small,
    },
    sizes: [bannerSize.small],
  },
  job_1: {
    adUnitCode: "job_1",
    mapping: {
      [threeXl]: [],
      [lg]: [],
      [xs]: bannerSize.small,
    },
    sizes: [bannerSize.small],
  },
  job_1_sidebar: {
    adUnitCode: "job_1",
    mapping: {
      [threeXl]: bannerSize.medium,
      [lg]: bannerSize.medium,
      [xs]: [],
    },
    sizes: [bannerSize.medium],
  },
  job_2: {
    adUnitCode: "job_2",
    mapping: {
      [threeXl]: [],
      [lg]: [],
      [xs]: bannerSize.small,
    },
    sizes: [bannerSize.small],
  },
  job_2_sidebar: {
    adUnitCode: "job_2",
    mapping: {
      [threeXl]: bannerSize.medium,
      [lg]: bannerSize.medium,
      [xs]: [],
    },
    sizes: [bannerSize.medium],
  },
  mediumrect_1: {
    adUnitCode: "mediumrect_1",
    mapping: {
      [threeXl]: [],
      [lg]: [],
      [xs]: bannerSize.small,
    },
    sizes: [bannerSize.small],
  },
  mediumrect_1_sidebar: {
    adUnitCode: "mediumrect_1",
    mapping: {
      [threeXl]: bannerSize.medium,
      [lg]: bannerSize.medium,
      [xs]: [],
    },
    sizes: [bannerSize.medium],
  },
  mediumrect_2: {
    adUnitCode: "mediumrect_2",
    mapping: {
      [threeXl]: bannerSize.medium,
      [lg]: bannerSize.medium,
      [xs]: bannerSize.small,
    },
    sizes: [bannerSize.small, bannerSize.medium],
  },
  mediumrect_2_mobile: {
    adUnitCode: "mediumrect_2",
    mapping: {
      [threeXl]: [],
      [lg]: [],
      [xs]: bannerSize.small,
    },
    sizes: [bannerSize.small],
  },
  mediumrect_3: {
    adUnitCode: "mediumrect_3",
    mapping: {
      [threeXl]: bannerSize.medium,
      [lg]: bannerSize.medium,
      [xs]: bannerSize.small,
    },
    sizes: [bannerSize.small, bannerSize.medium],
  },
  mediumrect_3_mobile: {
    adUnitCode: "mediumrect_3",
    mapping: {
      [threeXl]: [],
      [lg]: [],
      [xs]: bannerSize.small,
    },
    sizes: [bannerSize.small],
  },
  sticky: {
    adUnitCode: "sticky",
    mapping: {
      [threeXl]: bannerSize.sticky,
      [lg]: bannerSize.sticky,
      [xs]: [],
    },
    sizes: [bannerSize.sticky],
  },
  sticky_halfpage: {
    adUnitCode: "sticky_halfpage",
    mapping: {
      [threeXl]: [bannerSize.medium, bannerSize.large],
      [lg]: [],
      [xs]: [],
    },
    sizes: [bannerSize.medium, bannerSize.large],
  },
  top: {
    adUnitCode: "top",
    mapping: {
      [threeXl]: bannerSize.top,
      [lg]: bannerSize.top,
      [xs]: bannerSize.small,
    },
    sizes: [bannerSize.small, bannerSize.top],
  },
}
